<script setup lang="ts">
/**
 * A key or key combination as keycaps: `['Mod', 'K']` shows „⌘ K“ on a Mac and
 * „Strg + K“ elsewhere; a string („Mod+Shift+P“) works too. Each key is its
 * own `<kbd>` inside an outer `<kbd>`, the HTML way to write a combination.
 * `apple` forces one platform's notation (tests, docs).
 */
import { computed } from 'vue';
import { isApplePlatform, keyLabel, parseCombo, type NxKeyCombo } from './keys';

const props = withDefaults(
  defineProps<{
    keys: NxKeyCombo | string;
    /** Size of the caps. */
    size?: 'sm' | 'md';
    /** Force the Apple (⌘) or the other (Strg) notation. */
    apple?: boolean;
  }>(),
  { size: 'md', apple: undefined }
);

const apple = computed(() => props.apple ?? isApplePlatform());
const caps = computed(() => parseCombo(props.keys).map((k) => keyLabel(k, apple.value)));
</script>

<template>
  <kbd class="nx-kbd" :class="`size-${props.size}`">
    <template v-for="(cap, i) in caps" :key="i">
      <span v-if="i > 0 && !apple" class="nx-kbd__plus" aria-hidden="true">+</span>
      <kbd class="nx-kbd__cap">{{ cap }}</kbd>
    </template>
  </kbd>
</template>

<style scoped>
.nx-kbd {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: var(--nxt-space-2xs);
  font: inherit;
  white-space: nowrap;
}
.nx-kbd__cap {
  display: inline-grid;
  place-items: center;
  min-width: calc(var(--nxt-size-unit) * 1.4);
  height: calc(var(--nxt-size-unit) * 1.4);
  padding: 0 var(--nxt-space-xs);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-block-end-width: calc(var(--nxt-hairline) * 2);
  border-radius: var(--nxt-radius-sm);
  background: linear-gradient(var(--nxt-surface-raised-bg), var(--nxt-surface-sunken-bg));
  color: var(--nxt-fg);
  font-family: inherit;
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-medium);
  line-height: 1;
  box-shadow: var(--nxt-shadow-sm);
}
.nx-kbd.size-sm .nx-kbd__cap {
  min-width: calc(var(--nxt-size-unit) * 1.15);
  height: calc(var(--nxt-size-unit) * 1.15);
  padding: 0 var(--nxt-space-2xs);
  font-size: calc(var(--nxt-font-size-xs) * 0.9);
}
.nx-kbd__plus {
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
}
</style>
